/**
 * SalesActivities.tsx — Sales / Activities feed with Log Activity modal
 * Design: Golden Stack — clean card list, type-coloured icons, modal for all activity types
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 * Off-scale text-12 labels stay arbitrary by the GATE 0-A map.
 */
import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import {
  Plus, Mail, Phone, Users, FileText, CheckSquare,
  MoreHorizontal, Check, X, Link2, User
} from 'lucide-react'
import { SectionTitle, Avatar, LoadingState, ErrorState } from '../../DashboardShared'
import type { SalesActivity, SalesContact } from '../../types'
import { useAsync, fetchActivities, fetchContacts, createActivity, completeActivity, mapActivity } from '../../api'
import { apiErrorMessage } from '../../../../api/client'
import { buildActivityPayload, completesOnLog, type CreateActivityBody } from './activityLog'
import { toast } from 'sonner'
import { activityType, ink, overlay } from '../../../../theme/tokens'

// One literal, two call sites (the row's tick, and the complete that follows a
// logged call / email / meeting / note) — the same condition, so one sentence.
const MARK_DONE_FAILED = 'Failed to mark activity done'

// ── Type config ───────────────────────────────────────────────────────────────

const TYPE_CONFIG: Record<SalesActivity['type'], { icon: React.ReactNode; color: string; bg: string; label: string; description: string }> = {
  email:   { icon: <Mail size={13} />,        color: activityType.email.color,   bg: activityType.email.bg,   label: 'Email',   description: 'Log an email sent or received' },
  call:    { icon: <Phone size={13} />,       color: activityType.call.color,    bg: activityType.call.bg,    label: 'Call',    description: 'Log a phone or video call' },
  meeting: { icon: <Users size={13} />,       color: activityType.meeting.color, bg: activityType.meeting.bg, label: 'Meeting', description: 'Log an in-person or virtual meeting' },
  note:    { icon: <FileText size={13} />,    color: activityType.note.color,    bg: activityType.note.bg,    label: 'Note',    description: 'Add a note about this contact or deal' },
  task:    { icon: <CheckSquare size={13} />, color: activityType.task.color,    bg: activityType.task.bg,    label: 'Task',    description: 'Create a follow-up task' },
}

// ── Log Activity Modal ────────────────────────────────────────────────────────

interface LogActivityModalProps {
  onClose: () => void
  /** Performs the write. Resolves true once the activity EXISTS on the server
   *  (the drawer then closes), false when the create was refused or failed
   *  (the drawer stays open with the rep's input intact). */
  onSave: (body: CreateActivityBody, markDone: boolean, typeLabel: string) => Promise<boolean>
  contacts: SalesContact[]
}

function LogActivityModal({ onClose, onSave, contacts }: LogActivityModalProps) {
  const [activeType, setActiveType] = useState<SalesActivity['type']>('call')
  const [description, setDescription] = useState('')
  // The PICKED contact's id. Typing in the box clears it: a typed name is not
  // an id, and the API links a contact by id or not at all.
  const [contactId, setContactId] = useState('')
  const [deal, setDeal] = useState('')
  const [saving, setSaving] = useState(false)
  const [date, setDate] = useState('')
  const [time, setTime] = useState('')
  const [duration, setDuration] = useState('')
  const [outcome, setOutcome] = useState('')
  const [contactSearch, setContactSearch] = useState('')
  const [showContactDropdown, setShowContactDropdown] = useState(false)
  const contactRef = useRef<HTMLDivElement>(null)

  const contactOptions = contacts.filter(c =>
    c.name.toLowerCase().includes(contactSearch.toLowerCase()) ||
    c.company.toLowerCase().includes(contactSearch.toLowerCase())
  )

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (contactRef.current && !contactRef.current.contains(e.target as Node)) {
        setShowContactDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Reset per-type fields when type changes
  useEffect(() => { setDuration(''); setOutcome('') }, [activeType])

  function selectContact(c: SalesContact) {
    setContactId(c.id)
    setContactSearch(c.name)
    setShowContactDropdown(false)
  }

  // No toast here: success is the parent's to announce, and only after the
  // server has answered 2xx. A refused or failed save keeps the drawer open.
  async function handleSave(done: boolean) {
    if (saving) return
    const payload = buildActivityPayload({
      type: activeType, description, contactId, date, time, duration, outcome, deal,
    })
    if (!payload.ok) { toast.error(payload.error); return }
    setSaving(true)
    const saved = await onSave(payload.body, completesOnLog(activeType, done), TYPE_CONFIG[activeType].label)
    setSaving(false)
    if (saved) onClose()
  }

  const cfg = TYPE_CONFIG[activeType]
  const showDuration = activeType === 'call' || activeType === 'meeting'
  const showOutcome  = activeType === 'call' || activeType === 'meeting' || activeType === 'email'

  const OUTCOMES: Record<string, string[]> = {
    call:    ['Connected', 'No answer', 'Left voicemail', 'Wrong number', 'Busy'],
    email:   ['Sent', 'Replied', 'Bounced', 'Opened', 'No response'],
    meeting: ['Completed', 'Rescheduled', 'No show', 'Follow-up needed'],
  }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="k-drawer"
        style={{ width: 'min(600px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">Log Activity</div>
            <div className="text-12 text-ink-muted mt-1">Track your sales touchpoints</div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* ── Type tabs ── */}
        <div className="flex items-center gap-2 px-6 pb-4 flex-shrink-0 border-b border-edge-row">
          {(Object.keys(TYPE_CONFIG) as SalesActivity['type'][]).map(t => {
            const c = TYPE_CONFIG[t]
            const active = t === activeType
            return (
              <button key={t} onClick={() => setActiveType(t)}
                className={`flex items-center gap-2 px-4 py-3 rounded-full text-13 font-semibold transition-all flex-1 justify-center ${
                  active ? 'text-ink-on-accent' : 'text-ink-secondary hover:bg-surface-nav-hover'
                }`}
                style={active ? { background: c.color } : {}}>
                <span style={active ? { color: ink['on-accent'] } : { color: c.color }}>{c.icon}</span>
                <span>{c.label}</span>
              </button>
            )
          })}
        </div>

        {/* ── Form ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">

          {/* Description */}
          <div>
            <label className="block text-12 font-semibold text-ink-body mb-2">
              {activeType === 'note' ? 'Note' : activeType === 'task' ? 'Task' : 'Subject'}
              <span className="text-danger ml-1">*</span>
            </label>
            {activeType === 'note' ? (
              <textarea value={description} onChange={e => setDescription(e.target.value)}
                placeholder="Write your note here..."
                rows={3}
                className="w-full px-4 py-3 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body placeholder:text-ink-faint resize-none transition-all" />
            ) : (
              <input type="text" value={description} onChange={e => setDescription(e.target.value)}
                placeholder={
                  activeType === 'call' ? 'e.g. Discovery call — discussed pricing' :
                  activeType === 'email' ? 'e.g. Sent proposal follow-up' :
                  activeType === 'meeting' ? 'e.g. Product demo with engineering team' :
                  'e.g. Send case study by Friday'
                }
                className="w-full px-4 py-3 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body placeholder:text-ink-faint transition-all" />
            )}
          </div>

          {/* Contact */}
          <div ref={contactRef} className="relative">
            <label className="block text-12 font-semibold text-ink-body mb-2">
              Contact<span className="text-danger ml-1">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint"><User size={13} /></div>
              <input type="text" value={contactSearch}
                onChange={e => { setContactSearch(e.target.value); setContactId(''); setShowContactDropdown(true) }}
                onFocus={() => setShowContactDropdown(true)}
                placeholder="Search by name or company..."
                className="w-full pl-8 pr-4 py-3 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body placeholder:text-ink-faint transition-all" />
            </div>
            {showContactDropdown && contactOptions.length > 0 && (
              <div className="absolute z-20 top-full mt-1 left-0 right-0 bg-surface-card border border-edge-control rounded-control overflow-hidden" style={{ maxHeight: 180 }}>
                <div className="overflow-y-auto" style={{ maxHeight: 180 }}>
                  {contactOptions.slice(0, 6).map(c => (
                    <button key={c.id} onClick={() => selectContact(c)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-row-hover transition-colors text-left">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-ink-on-accent text-overline font-bold flex-shrink-0"
                        style={{ background: cfg.color }}>
                        {c.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                      <div className="min-w-0">
                        <div className="text-13 font-semibold text-ink-primary truncate">{c.name}</div>
                        <div className="text-11 text-ink-faint truncate">{c.company} · {c.title}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Date + Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-12 font-semibold text-ink-body mb-2">
                {activeType === 'task' ? 'Due Date' : 'Date'}
              </label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)}
                className="w-full px-4 py-3 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body transition-all" />
            </div>
            <div>
              <label className="block text-12 font-semibold text-ink-body mb-2">
                {activeType === 'task' ? 'Due Time' : 'Time'}
              </label>
              <input type="time" value={time} onChange={e => setTime(e.target.value)}
                className="w-full px-4 py-3 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body transition-all" />
            </div>
          </div>

          {/* Duration */}
          {showDuration && (
            <div>
              <label className="block text-12 font-semibold text-ink-body mb-2">Duration</label>
              <div className="flex flex-wrap gap-2">
                {['15 min', '30 min', '45 min', '1 hour', '1.5 hrs', '2 hrs'].map(d => (
                  <button key={d} onClick={() => setDuration(duration === d ? '' : d)}
                    className={`px-3 py-2 rounded-full text-12 font-medium border transition-colors ${
                      duration === d
                        ? 'border-transparent text-ink-on-accent'
                        : 'bg-surface-control text-ink-secondary border-edge-control hover:border-ink-disabled'
                    }`}
                    style={duration === d ? { background: cfg.color, borderColor: cfg.color } : {}}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Outcome */}
          {showOutcome && (
            <div>
              <label className="block text-12 font-semibold text-ink-body mb-2">Outcome</label>
              <div className="flex flex-wrap gap-2">
                {(OUTCOMES[activeType] || []).map(o => (
                  <button key={o} onClick={() => setOutcome(outcome === o ? '' : o)}
                    className={`px-3 py-2 rounded-full text-12 font-medium border transition-colors ${
                      outcome === o
                        ? 'border-transparent text-ink-on-accent'
                        : 'bg-surface-control text-ink-secondary border-edge-control hover:border-ink-disabled'
                    }`}
                    style={outcome === o ? { background: cfg.color, borderColor: cfg.color } : {}}>
                    {o}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Linked deal */}
          <div>
            <label className="block text-12 font-semibold text-ink-body mb-2">Linked Deal <span className="text-ink-faint font-normal">(optional)</span></label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint"><Link2 size={13} /></div>
              <input type="text" value={deal} onChange={e => setDeal(e.target.value)}
                placeholder="e.g. Nile Site Revamp"
                className="w-full pl-8 pr-4 py-3 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body placeholder:text-ink-faint transition-all" />
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-between gap-3 px-6 pt-5 pb-14 border-t border-edge-row flex-shrink-0">
          <button onClick={onClose} className="k-btn-secondary">
            Cancel
          </button>
          <div className="flex items-center gap-2">
            {activeType !== 'note' && activeType !== 'task' && (
              <button onClick={() => handleSave(false)} disabled={saving} className="k-btn-secondary">
                Schedule
              </button>
            )}
            <button onClick={() => handleSave(true)} disabled={saving}
              className="k-btn k-btn--lg text-ink-on-accent"
              style={{ background: cfg.color }}>
              Log {cfg.label}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

/** The contacts read's own cap — the picker's list AND the name lookup for
 *  the rows read the same page; named in the fallback note so the bound is
 *  stated (OwnerSelect's DIRECTORY_CAP shape). */
const CONTACTS_CAP = 200

export function SalesActivities() {
  const { data, loading, error } = useAsync(
    () => Promise.all([fetchActivities(), fetchContacts({ limit: CONTACTS_CAP })]),
    [],
  )
  const [activities, setActivities] = useState<SalesActivity[]>([])
  const [typeFilter, setTypeFilter] = useState<'all' | SalesActivity['type']>('all')
  const [showDone, setShowDone] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [marking, setMarking] = useState<Record<string, boolean>>({})

  // Seed local state from the fetched list (local state also holds optimistic adds).
  useEffect(() => { if (data) setActivities(data[0]) }, [data])

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const contacts = data?.[1] ?? []
  // The wire names a contact by id only; the name comes from the contacts
  // read the picker already needs. An id the page does not carry renders as
  // the RAW id with a note naming the bound — never a blank, which would read
  // the same as "no contact" (the §35 ruling-3 / OwnerSelect rule).
  const contactById = new Map(contacts.map(c => [c.id, c]))

  const filtered = activities.filter(a => {
    if (typeFilter !== 'all' && a.type !== typeFilter) return false
    if (!showDone && a.done) return false
    return true
  })

  const pending = activities.filter(a => !a.done)
  const done    = activities.filter(a => a.done)

  // Both writes go to the routes Deal detail already uses. The row on screen
  // is always the SERVER's row (mapActivity of the response), never a
  // client-made one, and a success toast fires only after a confirmed 2xx.
  async function handleMarkDone(id: string) {
    if (marking[id]) return
    setMarking(prev => ({ ...prev, [id]: true }))
    try {
      const updated = await completeActivity(id)
      setActivities(prev => prev.map(a => a.id === id ? mapActivity(updated) : a))
      toast.success('Activity marked as done')
    } catch (e) {
      toast.error(apiErrorMessage(e, MARK_DONE_FAILED))
    } finally {
      setMarking(prev => { const next = { ...prev }; delete next[id]; return next })
    }
  }

  async function handleLogActivity(body: CreateActivityBody, markDone: boolean, typeLabel: string): Promise<boolean> {
    let created
    try {
      created = await createActivity(body)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to log activity'))
      return false
    }
    // The activity EXISTS from here on. If the follow-up complete fails, the
    // row is shown as the server holds it — pending — beside a real error.
    if (markDone) {
      try {
        created = await completeActivity(created.id)
      } catch (e) {
        setActivities(prev => [mapActivity(created!), ...prev])
        toast.error(apiErrorMessage(e, MARK_DONE_FAILED))
        return true
      }
    }
    setActivities(prev => [mapActivity(created!), ...prev])
    toast.success(`${typeLabel} logged successfully`)
    return true
  }

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title="Activities"
        subtitle={`${pending.length} pending · ${done.length} completed today`}
        action={
          <button className="k-btn k-btn--lg inline-flex items-center gap-2" onClick={() => setShowModal(true)}>
            <Plus size={14} /> Log Activity
          </button>
        }
      />

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {(['all', 'email', 'call', 'meeting', 'note', 'task'] as const).map(t => {
          const count = t === 'all' ? activities.length : activities.filter(a => a.type === t).length
          const cfg = t !== 'all' ? TYPE_CONFIG[t] : null
          return (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              className={`px-3 py-2 rounded-full text-12 font-medium transition-colors flex items-center gap-2 ${typeFilter === t ? 'bg-ink-primary text-ink-on-accent border border-ink-primary' : 'bg-surface-control text-ink-secondary hover:bg-surface-nav-hover border border-edge-control hover:border-edge-control-hover'}`}
            >
              {cfg && <span style={{ color: cfg.color }}>{cfg.icon}</span>}
              <span className="capitalize">{t === 'all' ? 'All' : cfg!.label}</span>
              <span className={`text-overline font-mono px-2 py-1 rounded-full ${typeFilter === t ? 'bg-accent text-ink-on-accent' : 'bg-surface-panel text-ink-faint'}`}>{count}</span>
            </button>
          )
        })}
        <button
          onClick={() => setShowDone(!showDone)}
          className={`ml-auto px-3 py-2 rounded-full text-12 font-medium transition-colors border ${showDone ? 'bg-surface-control text-ink-secondary border-edge-control hover:border-edge-control-hover' : 'bg-ink-primary text-ink-on-accent border-ink-primary'}`}
        >
          {showDone ? 'Hide completed' : 'Show completed'}
        </button>
      </div>

      {/* Activity list */}
      <div className="k-card">
        <div className="divide-y divide-edge-row">
          {filtered.length === 0 ? (
            <div className="px-5 py-10 text-center text-13 text-ink-muted">No activities match your filter.</div>
          ) : (
            filtered.map(a => {
              const cfg = TYPE_CONFIG[a.type]
              const contact = a.contactId ? contactById.get(a.contactId) : undefined
              return (
                <div key={a.id} className={`flex items-start gap-4 px-5 py-4 hover:bg-surface-row-hover transition-colors ${a.done ? 'opacity-60' : ''}`}>
                  {/* Type icon */}
                  <div className="flex-shrink-0 w-8 h-8 rounded-control flex items-center justify-center mt-1" style={{ background: cfg.bg, color: cfg.color }}>
                    {cfg.icon}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className={`text-13 font-medium leading-snug ${a.done ? 'line-through text-ink-muted' : 'text-ink-primary'}`}>
                          {a.subject}
                        </p>
                        {a.description && (
                          <p className="text-12 text-ink-secondary mt-1 leading-snug">{a.description}</p>
                        )}
                        {/* Only what the row has. Three contact cases: no
                            contactId on the wire → no contact cell; resolved →
                            avatar + name (+ company when the record has one);
                            an id past the contacts read → the raw id and the
                            bound, no avatar (initials cut from a UUID would be
                            invented). The deal is always its raw id — the wire
                            has no title and the page holds no deals read. */}
                        {(a.contactId || a.deal) && (
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {contact && (
                              <>
                                <Avatar name={contact.name} size={18} />
                                <span className="text-12 text-ink-secondary">{contact.name}</span>
                                {contact.company && (
                                  <>
                                    <span className="text-ink-disabled">·</span>
                                    <span className="text-12 text-ink-faint">{contact.company}</span>
                                  </>
                                )}
                              </>
                            )}
                            {a.contactId && !contact && (
                              <span className="text-12 text-ink-secondary">{a.contactId} — not in the contacts read (first {CONTACTS_CAP} contacts)</span>
                            )}
                            {a.deal && (
                              <>
                                {a.contactId && <span className="text-ink-disabled">·</span>}
                                <span className="text-11 bg-surface-panel text-ink-secondary px-2 py-1 rounded-full">{a.deal}</span>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-11 text-ink-faint">{a.time}</span>
                        <span className="text-11 text-ink-faint">by {a.owner}</span>
                        {!a.done && (
                          <button
                            onClick={() => handleMarkDone(a.id)}
                            disabled={Boolean(marking[a.id])}
                            className="w-6 h-6 rounded-full border-2 border-edge-control hover:border-accent hover:bg-surface-panel transition-colors flex items-center justify-center"
                            title="Mark as done"
                          >
                            <Check size={11} className="text-ink-faint" />
                          </button>
                        )}
                        {a.done && (
                          <div className="w-6 h-6 rounded-full bg-success-bg flex items-center justify-center">
                            <Check size={11} className="text-success" />
                          </div>
                        )}
                        <button onClick={() => toast.info('Activity options coming soon')} className="p-1 rounded hover:bg-surface-nav-hover transition-colors">
                          <MoreHorizontal size={14} className="text-ink-faint" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* Log Activity Modal */}
      {showModal && (
        <LogActivityModal
          onClose={() => setShowModal(false)}
          onSave={handleLogActivity}
          contacts={contacts}
        />
      )}
    </div>
  )
}
